import type { Attachment } from "@sd/types/ticket";
import { collectAttachmentMediaPathsFromRecord } from "@sd/lib/attachmentMediaUrl";

export function looksLikeImageAttachment(name: string, mime: string): boolean {
  const m = (mime ?? "").toLowerCase();
  if (m.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|avif|bmp|svg)$/i.test(name);
}

export function looksLikeVideoAttachment(name: string, mime: string): boolean {
  const m = (mime ?? "").toLowerCase();
  if (m.startsWith("video/")) return true;
  return /\.(webm|mp4|ogg|mov|avi|mkv)$/i.test(name);
}

export function looksLikePdfAttachment(name: string, mime: string): boolean {
  const m = (mime ?? "").toLowerCase();
  if (m.includes("pdf")) return true;
  return /\.pdf$/i.test(name);
}

export function looksLikeSpreadsheetAttachment(
  name: string,
  mime: string
): boolean {
  const m = (mime ?? "").toLowerCase();
  if (m.includes("spreadsheet") || m.includes("excel")) return true;
  return /\.xlsx?$/i.test(name);
}

export function looksLikeDocAttachment(name: string, mime: string): boolean {
  const m = (mime ?? "").toLowerCase();
  if (m.includes("msword") || m.includes("wordprocessingml")) return true;
  return /\.docx?$/i.test(name);
}

export function looksLikeZipAttachment(name: string, mime: string): boolean {
  const m = (mime ?? "").toLowerCase();
  if (m.includes("zip")) return true;
  return /\.zip$/i.test(name);
}

export function attachmentKindLabel(
  name: string,
  mime: string
): "Image" | "Video" | "Pdf" | "Doc" | "Zip" | "Excel" | "File" {
  if (looksLikeImageAttachment(name, mime)) return "Image";
  if (looksLikeVideoAttachment(name, mime)) return "Video";
  if (looksLikePdfAttachment(name, mime)) return "Pdf";
  if (looksLikeDocAttachment(name, mime)) return "Doc";
  if (looksLikeZipAttachment(name, mime)) return "Zip";
  if (looksLikeSpreadsheetAttachment(name, mime)) return "Excel";
  return "File";
}

export function formatAttachmentSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function attachmentMediaPaths(file: Attachment): {
  filePath: string;
  fallbackPaths: string[];
} {
  const mediaPaths = collectAttachmentMediaPathsFromRecord(file, false).filter(
    p => !/thumbnail|thumb|_tn\b|\/tn\//i.test(p)
  );
  const filePath = mediaPaths[0] ?? file.url;
  return {
    filePath: String(filePath),
    fallbackPaths: mediaPaths.slice(1),
  };
}
